import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Link, ShieldCheck, Unlink } from 'lucide-react';
import { AppleIcon } from '@/components/icons/apple-icon';
import { GoogleIcon } from '@/components/icons/google-icon';
import { LinkedinIcon } from '@/components/icons/linkedin-icon';
import { createFileRoute } from '@tanstack/react-router';
import { useAuthenticationDispatch, useAuthenticationState } from '@/hooks/use-authentication';
import { useMutation } from '@tanstack/react-query';
import { disconnectOAuth } from '@/services/user';
import { toast } from 'sonner';
import { errorHandler } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
const PROVIDERS = [
    { key: 'apple', name: 'Apple', icon: <AppleIcon className="h-5 w-5"/>, hide: false },
    { key: 'google', name: 'Google', icon: <GoogleIcon className="h-5 w-5"/>, hide: false },
    { key: 'linkedin', name: 'LinkedIn', icon: <LinkedinIcon className="h-5 w-5"/>, hide: false },
];
export const Route = createFileRoute('/console/_authenticated/_layout/profile/_layout/connections/')({
    component: ConnectionsPage,
});
function ConnectionsPage() {
    const { t } = useTranslation('profile');
    const { user } = useAuthenticationState();
    const dispatch = useAuthenticationDispatch();
    const [provider, setProvider] = React.useState();
    const { mutate, isPending } = useMutation({
        mutationFn: disconnectOAuth,
        onSuccess: (data) => {
            toast.success(data.message);
            setProvider(undefined);
            dispatch({ type: 'UPDATE_USER', user: data.user });
        },
        onError: errorHandler,
    });
    if (!user)
        return null;
    return (<Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-primary"/>
                    {t('connections.title')}
                </CardTitle>
                <CardDescription>{t('connections.desc')}</CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
                {PROVIDERS.map((p, index) => {
            if (p.hide)
                return null;
            const isConnected = user.authentications[p.key];
            return (<React.Fragment key={p.key}>
                            <div className="flex items-center justify-between gap-4">
                                {/* Left */}
                                <div className="flex items-center gap-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-full border bg-muted">
                                        {p.icon}
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">{p.name}</span>
                                        {user.authentications[p.key] ? (<Badge variant="secondary">{t('connections.connected')}</Badge>) : (<Badge variant="outline">{t('connections.not_connected')}</Badge>)}
                                    </div>
                                </div>

                                {/* Right */}
                                {isConnected ? (<Button variant="outline" size="sm" className="gap-2" disabled={isPending} onClick={() => {
                        setProvider(p.key);
                        mutate(p.key);
                    }}>
                                        <Unlink className="h-4 w-4"/>
                                        {isPending && provider === p.key
                        ? t('connections.disconnecting')
                        : t('connections.disconnect')}
                                    </Button>) : (<Button size="sm" className="gap-2" disabled={isPending} asChild>
                                        <a href={`/oauth/connect/${p.key}`}>
                                            <Link className="h-4 w-4"/>
                                            {t('connections.connect')}
                                        </a>
                                    </Button>)}
                            </div>

                            {index < PROVIDERS.length - 1 && <Separator />}
                        </React.Fragment>);
        })}
            </CardContent>
        </Card>);
}
